import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import generateUniqueId from '@insight/toolkit-utils/lib/generators/generateUniqueId';
import { CustomTypes } from '../utils';
import SUPPORTED_ICONS from './SUPPORTED_ICONS';

/**
 * Icons fall into one of two types: presentation and semantic. The default state
 * is presentational, meaning it adds no relevance and is mostly used for design.
 * These are hidden from screen readers
 *
 * Semantic icons provide meaning in absence of text (Think modal close button)
 * These are readable by screen-readers and need to provide the proper title
 * prop to convey their meaning
 */
const Icon = ({ className, icon, role, title, size, type, ...rest }) => {
  const classNames = cn(
    'c-icon',
    { [`c-icon--${size}`]: size },
    { [`c-icon--${type}`]: type },
    className
  );

  const sharedProps = {
    className: classNames,
    role,
  };

  if (title) {
    const id = generateUniqueId('icon');
    return (
      <svg
        {...sharedProps}
        {...rest}
        alt={title}
        aria-labelledby={id}
        role="img"
      >
        <title id={id} className="c-icon__title">
          {title}
        </title>
        <use xlinkHref={`#ds-icon-${icon}`} />
      </svg>
    );
  }

  return (
    <svg {...sharedProps} {...rest} aria-hidden="true">
      <use xlinkHref={`#ds-icon-${icon}`} />
    </svg>
  );
};

export default Icon;

Icon.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.oneOf(SUPPORTED_ICONS).isRequired,
  role: CustomTypes.accessibleImageRole,
  title: PropTypes.string,
  size: PropTypes.oneOf(['small', 'large']),
  type: PropTypes.oneOf([
    'alert',
    'info',
    'warning',
    'error',
    'success',
    'ghost',
  ]),
};

Icon.defaultProps = {
  className: '',
  role: 'presentation',
  title: '',
  size: null,
  type: null,
};
